import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FinishedOutOrder, FinishedOutOrderSkuGroup, OutOrderComments } from 'src/models/OutOrder';
import { OutOrderDataSourceV3Service } from 'src/app/core/data-sources/v3/out-orders/out-order-data-source-v3.service';
import { FormControl } from '@angular/forms';
import { ActionService } from 'src/app/core/services/action.service';
import * as moment from 'moment';
import { UserDataSourceService } from 'src/app/core/data-sources/user-data-source.service';
import { ErrorsEnum, ErrorService } from 'src/app/core/services/error.service';

@Component({
  selector: 'app-sell-order-details-modal',
  templateUrl: './sell-order-details-modal.component.html',
  styleUrls: ['./sell-order-details-modal.component.scss']
})
export class SellOrderDetailsModalComponent implements OnInit {
  orderDetails: FinishedOutOrder;
  sellOrder: FinishedOutOrder;
  selected = new FormControl(0);
  comments: OutOrderComments[] = [];
  manifestDisplayedColumns = ['sku', 'name', 'quantity'];
  manifestColumnsDefinition = [
    { key: 'sku', text: 'Código do Item' },
    { key: 'name', text: 'Descrição' },
    { key: 'quantity', text: 'Quantidade', alignRight: true }
  ];
  title = 'Detalhes da ordem';
  showPalletHistory = false;
  crateId: string;

  constructor(
    private errorService: ErrorService,
    private userDataSource: UserDataSourceService,
    private actionService: ActionService,
    private outOrderDataSourceV3Service: OutOrderDataSourceV3Service,
    public dialogRef: MatDialogRef<SellOrderDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sellOrder: FinishedOutOrder, page: number }
  ) { }

  async ngOnInit(): Promise<void> {
    this.selected.setValue(this.data.page || 0);
    this.sellOrder = this.data.sellOrder;
    this.sellOrder.skuGroups = this.groupProductsBySku(this.sellOrder?.skuGroups || []);
    this.orderDetails = await this.outOrderDataSourceV3Service.getFinishedOutOrderDetails(this.data.sellOrder?.id);
    this.sellOrder.outOrderCrates = this.orderDetails?.outOrderCrates || [];
    this.getComments();
  }

  close(): void {
    this.dialogRef.close();
  }

  timestampToDate(timestamp) {
    if (!timestamp) {
      return '---';
    }
    return moment(timestamp).format('DD/MM/YYYY - HH:mm');
  }

  onSelectedIndexChange(event) {
    this.selected.setValue(event);
  }

  get manifestTableData() {
    return (this.orderDetails?.manifest || []).map(product => ({
      sku: product?.sku || '---',
      name: product?.name || '---',
      quantity: product?.quantity ?? '---'
    }));
  }

  async submitComment(event) {
    try {
      const payload = {
        outOrderId: this.sellOrder.id,
        stage: "DISPATCHING",
        text: event.comment,
        timestamp: Date.now()
      };
      await this.actionService.addCommentToOutOrder(payload);
    } catch (e) {
      this.handleError(e);
    } finally {
      await this.getComments();
    }
  }

  async deleteComment(id) {
    try {
      await this.actionService.removeCommentFromOutOrder({ id });
    } catch (e) {
      this.handleError(e);
    } finally {
      await this.getComments();
    }
  }

  async getComments() {
    const comments = await this.outOrderDataSourceV3Service.getFinishedOutOrderCommentsByOrderId(this.data.sellOrder?.id);
    const userIds = Array.from(new Set(comments.map(comment => comment.ownerId)));
    if (userIds.length === 0) {
      this.comments = comments;
      return;
    }
    const users = await this.userDataSource.v3GetUserByIdWWithArray(userIds);
    for (const comment of comments) {
      const user = users.values.find(u => u.id === comment.ownerId);
      comment.user = { firstName: user.firstName, lastName: user.lastName };
    }
    this.comments = comments;
  }

  handleError(err) {
    const error = err?.error?.body;
    if (!error || !error.type) return;

    switch (error.type) {
      case ErrorsEnum.COMMENT_ERROR_NOT_FOUND:
        this.errorService.openErrorSnackBarWithCustomMessage(`Comentário não encontrado.`);
        break;
      case ErrorsEnum.COMMENT_ERROR_USER_NOT_ALLOWED:
        this.errorService.openErrorSnackBarWithCustomMessage(`Você não tem permissão para excluir este comentário.`);
        break;
      case ErrorsEnum.COMMENT_ERROR_OUT_ORDER_NOT_FOUND:
        this.errorService.openErrorSnackBarWithCustomMessage(`Ordem de saída não encontrada. Atualize a página e tente novamente.`);
        break;
    }
  }

  groupProductsBySku(products) {
    const groupedProducts = {};
    for (const product of products) {
      if (!groupedProducts[product.sku]) {
        groupedProducts[product.sku] = {
          sku: product.sku,
          productName: product.productName,
          packSize: 0,
          quantity: 0
        };
      }
      groupedProducts[product.sku].quantity += product.quantity;
      groupedProducts[product.sku].packSize += product.packSize;
    }
    return Object.values(groupedProducts) as FinishedOutOrderSkuGroup[];
  }

  goToPalletHistory(crateId: string) {
    this.crateId = crateId;
    this.showPalletHistory = true;
    this.title = 'Histórico do Pallet';
  }

  goToOrderDetails() {
    this.showPalletHistory = false;
    this.title = 'Detalhes da ordem';
    this.crateId = null;
  }

}
